/**
 * Membership Manager
 * Handles membership state, popup interactions, and cart synchronization
 */

import { ThemeEvents } from '@theme/events';

class MembershipManager {
  constructor() {
    this.membershipActive = false;
    this.membershipProductHandle = window.Shopify?.theme?.settings?.membership_product_handle || 'membership';
    this.allowNextAddToCart = false;
    this.init();
  }

  async init() {
    console.log('[MembershipManager] Initializing...');

    // Intercept fetch requests FIRST (before anything else)
    this.interceptFetchRequests();

    // Detect initial membership status
    await this.detectMembershipStatus();
    console.log('[MembershipManager] Initial membership status:', this.membershipActive);

    // Initialize UI
    this.initializeCardSelectors();
    this.initializePopup();
    this.initializeAddToCart();

    // Update UI based on initial membership status
    this.updateUIBasedOnMembership();

    // Validate cart immediately on load
    await this.validateCartNow();

    // Listen for cart updates
    this.listenForCartUpdates();

    console.log('[MembershipManager] Initialization complete');
  }

  interceptFetchRequests() {
    const self = this;

    // Intercept fetch API
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      // Check if this is a cart add request
      if (url.includes('/cart/add')) {
        // Allow if we've set the bypass flag
        if (self.allowNextAddToCart) {
          self.allowNextAddToCart = false; // Reset flag
          return originalFetch.apply(this, arguments);
        }

        // Check if member variant is selected without membership
        const memberCard = document.querySelector('[data-membership-card="member"]');
        if (memberCard) {
          const radio = memberCard.querySelector('input[type="radio"]');
          if (radio && radio.checked && !self.membershipActive) {
            // Block the request and show popup
            self.showPopup();
            // Return a rejected promise to prevent the add to cart
            return Promise.reject(new Error('Membership required'));
          }
        }
      }

      // Allow the request to proceed
      return originalFetch.apply(this, arguments);
    };

    // Intercept XMLHttpRequest
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      // Store the URL for later checking
      this._url = url;
      return originalOpen.apply(this, [method, url, ...args]);
    };

    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(body) {
      // Check if this is a cart add request
      if (this._url && this._url.includes('/cart/add')) {
        // Allow if we've set the bypass flag
        if (self.allowNextAddToCart) {
          self.allowNextAddToCart = false; // Reset flag
          return originalSend.apply(this, arguments);
        }

        // Check if member variant is selected without membership
        const memberCard = document.querySelector('[data-membership-card="member"]');
        if (memberCard) {
          const radio = memberCard.querySelector('input[type="radio"]');
          if (radio && radio.checked && !self.membershipActive) {
            // Block the request and show popup
            self.showPopup();
            // Abort the request
            this.abort();
            return;
          }
        }
      }

      // Allow the request to proceed
      return originalSend.apply(this, arguments);
    };
  }

  async detectMembershipStatus() {
    // 1. Check global customer tag first (available on all pages via theme.liquid)
    // This is the most reliable source of truth and works on all pages
    if (window.customerHasActiveMemberTag === true) {
      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');
      console.log('[MembershipManager] Customer has active-member tag');
      return;
    }

    // 2. Check data-membership-state element (only on product pages with membership selector)
    const membershipStateEl = document.querySelector('[data-membership-state]');
    if (membershipStateEl && membershipStateEl.value === 'true') {
      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');
      console.log('[MembershipManager] Found membership-state element');
      return;
    }

    // 3. Check sessionStorage (fast cache to prevent race conditions during page transitions)
    const cachedStatus = sessionStorage.getItem('membershipActive');
    if (cachedStatus === 'true') {
      // Use cached value temporarily, but still verify with cart check
      this.membershipActive = true;
    }

    // 4. Check cart for membership product (async, but most accurate for cart-based membership)
    const hasInCart = await this.checkCartForMembership();
    this.membershipActive = hasInCart;
    sessionStorage.setItem('membershipActive', hasInCart ? 'true' : 'false');
    
    if (hasInCart) {
      console.log('[MembershipManager] Membership found in cart');
    } else {
      console.log('[MembershipManager] No membership found');
    }
  }

  async checkCartForMembership() {
    // First check global customer tag (available on all pages, most reliable)
    if (window.customerHasActiveMemberTag === true) {
      return true;
    }

    // Then check data-membership-state element (only on product pages)
    const membershipStateEl = document.querySelector('[data-membership-state]');
    if (membershipStateEl && membershipStateEl.value === 'true') {
      return true;
    }

    // Finally check cart for membership product
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const hasMembership = cart.items.some(item => {
        return item.product_type === 'Membership' ||
               (item.tags && item.tags.includes('site-membership')) ||
               item.handle === this.membershipProductHandle;
      });
      
      return hasMembership;
    } catch (error) {
      console.error('[ERROR] Check cart failed:', error);
      return false;
    }
  }

  initializeCardSelectors() {
    const cards = document.querySelectorAll('[data-membership-card]');

    cards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (card.classList.contains('membership-card--disabled')) {
          e.preventDefault();
          return;
        }

        const radio = card.querySelector('input[type="radio"]');
        if (radio && !radio.disabled) {
          radio.checked = true;
          this.handleCardSelection(card);
        }
      });
    });
  }

  handleCardSelection(selectedCard) {
    const container = selectedCard.closest('[data-membership-selector]');
    const allCards = container.querySelectorAll('[data-membership-card]');

    // Update visual states
    allCards.forEach(card => {
      card.classList.remove('membership-card--selected');
    });
    selectedCard.classList.add('membership-card--selected');

    // Update variant selection
    const variantId = selectedCard.dataset.variantId;
    if (variantId) {
      this.updateSelectedVariant(variantId);
    }
  }

  updateSelectedVariant(variantId) {
    // Find and trigger the main variant selector
    const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
    if (variantInput) {
      variantInput.checked = true;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // If no radio input, update select or hidden input
      const variantSelect = document.querySelector('select[name="id"]');
      const variantHidden = document.querySelector('input[type="hidden"][name="id"]');

      if (variantSelect) {
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (variantHidden) {
        variantHidden.value = variantId;
        variantHidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  initializePopup() {
    const popup = document.querySelector('[data-membership-popup]');
    if (!popup) return;

    // Close button
    const closeBtn = popup.querySelector('[data-membership-popup-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup(true));
    }

    // Overlay click
    const overlay = popup.querySelector('[data-membership-popup-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup(true));
    }

    // Action buttons
    const actions = popup.querySelectorAll('[data-membership-action]');
    actions.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.membershipAction;
        this.handlePopupAction(action);
      });
    });
  }

  showPopup() {
    const popup = document.querySelector('[data-membership-popup]');
    if (popup) {
      popup.style.display = 'block';
      document.body.style.overflow = 'hidden';
    }
  }

  closePopup(switchToRegular = false) {
    const popup = document.querySelector('[data-membership-popup]');
    if (popup) {
      popup.style.display = 'none';
      document.body.style.overflow = '';
    }

    if (switchToRegular) {
      // Select non-member card
      const nonMemberCard = document.querySelector('[data-membership-card="non-member"]');
      if (nonMemberCard) {
        const radio = nonMemberCard.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          this.handleCardSelection(nonMemberCard);
        }
      }
    }
  }

  async handlePopupAction(action) {
    switch (action) {
      case 'add-membership':
        await this.addMembershipAndProduct();
        break;
      case 'login':
        this.redirectToLogin();
        break;
      case 'switch-to-regular':
        this.closePopup(true);
        break;
    }
  }

  async addMembershipAndProduct() {
    // Set flag IMMEDIATELY to prevent validation
    sessionStorage.setItem('skipValidation', Date.now().toString());

    const addButton = document.querySelector('[data-membership-action="add-membership"]');
    const originalText = addButton ? addButton.textContent : '';

    // Helper to fetch with timeout using Promise.race
    const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Request timeout after ${timeout}ms`)), timeout)
      );

      const fetchPromise = fetch(url, options);

      return Promise.race([fetchPromise, timeoutPromise]);
    };

    try {
      // Disable button and show loading
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = 'Adding to cart...';
        addButton.style.opacity = '0.6';
        addButton.style.cursor = 'not-allowed';
      }

      console.log('[DEBUG] Step 1: Fetching membership product...');
      const membershipProduct = await fetchWithTimeout(`/products/${this.membershipProductHandle}.js`)
        .then(r => r.json());

      console.log('[DEBUG] Step 2: Getting variant ID...');
      const memberCard = document.querySelector('[data-membership-card="member"]');
      const productVariantId = memberCard ? memberCard.dataset.variantId : null;

      if (!productVariantId) {
        throw new Error('Could not find product variant');
      }

      console.log('[DEBUG] Step 3: Fetching current cart...');
      const currentCart = await fetchWithTimeout('/cart.js').then(r => r.json());
      console.log('[DEBUG] Current cart has', currentCart.items.length, 'items');

      // Build items list
      const itemsToAdd = [
        { id: membershipProduct.variants[0].id, quantity: 1 },
        { id: parseInt(productVariantId), quantity: 1 }
      ];

      // Convert existing non-member items (limit to prevent hanging)
      console.log('[DEBUG] Step 4: Converting existing items...');
      let converted = 0;
      for (const item of currentCart.items) {
        if (item.product_type === 'Membership' || (item.tags && item.tags.includes('site-membership'))) {
          continue;
        }

        const isNonMember = item.variant_title && item.variant_title.includes('Non-Member');

        if (isNonMember) {
          try {
            console.log('[DEBUG] Converting:', item.title);
            const product = await fetchWithTimeout(`/products/${item.handle}.js`, {}, 5000).then(r => r.json());
            const memberVariant = product.variants.find(v => {
              const targetTitle = item.variant_title.replace(' / Non-Member', ' / Member');
              return v.title === targetTitle;
            });

            if (memberVariant) {
              itemsToAdd.push({ id: memberVariant.id, quantity: item.quantity });
              converted++;
            } else {
              itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
            }
          } catch (err) {
            console.warn('[DEBUG] Failed to convert item, keeping original:', err);
            itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
          }
        } else {
          itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
        }
      }
      console.log('[DEBUG] Converted', converted, 'items. Total items to add:', itemsToAdd.length);

      // Clear and add
      console.log('[DEBUG] Step 5: Clearing cart...');
      await fetchWithTimeout('/cart/clear.js', { method: 'POST' });

      console.log('[DEBUG] Step 6: Adding all items...');
      this.allowNextAddToCart = true;
      await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      console.log('[DEBUG] Step 7: Items added successfully');

      // Update state
      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');

      // Redirect
      console.log('[DEBUG] Step 8: Redirecting to cart');
      this.closePopup(false);
      window.location.href = '/cart';

    } catch (error) {
      console.error('[ERROR] Add membership failed:', error.message);
      alert(`Error: ${error.message}. Please try again.`);

      sessionStorage.removeItem('skipValidation');

      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = originalText;
        addButton.style.opacity = '';
        addButton.style.cursor = '';
      }
    }
  }

  redirectToLogin() {
    const currentUrl = window.location.pathname;
    window.location.href = `/account/login?return_url=${encodeURIComponent(currentUrl)}`;
  }

  initializeAddToCart() {
    // Intercept add to cart clicks using CAPTURE PHASE to run before Fabric theme's handlers
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.getAttribute('action') === '/cart/add' || form.closest('[data-product-form]')) {
        this.handleAddToCart(e);
      }
    }, true); // Capture phase

    // Also handle button clicks (for AJAX carts) - capture phase
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[name="add"], [data-add-to-cart]');
      if (btn) {
        this.handleAddToCart(e);
      }
    }, true); // Capture phase
  }

  handleAddToCart(e) {
    // Check if member variant is selected and membership is NOT active
    const memberCard = document.querySelector('[data-membership-card="member"]');
    if (!memberCard) return;

    const radio = memberCard.querySelector('input[type="radio"]');
    if (radio && radio.checked && !this.membershipActive) {
      // Member price selected but no membership - show popup
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Prevent any other handlers from running
      this.showPopup();
      return false;
    }
  }

  listenForCartUpdates() {
    // Listen for theme cart update events
    const eventName = ThemeEvents.cartUpdate;
    console.log('[MembershipManager] Setting up cart update listener for event:', eventName);

    if (!eventName) {
      console.error('[MembershipManager] ERROR: ThemeEvents.cartUpdate is undefined!');
      return;
    }

    document.addEventListener(eventName, (event) => {
      console.log('[MembershipManager] CartUpdate event received:', event);
      this.handleCartUpdate();
    });

    // On cart page, check immediately on load (no delay for speed)
    if (window.location.pathname.includes('/cart')) {
      console.log('[MembershipManager] On cart page - validating immediately');
      this.validateCartNow();
    }
  }

  async handleCartUpdate() {
    console.log('[MembershipManager] handleCartUpdate called');
    const wasActive = this.membershipActive;
    const isActive = await this.checkCartForMembership();

    console.log('[MembershipManager] wasActive:', wasActive, 'isActive:', isActive);

    this.membershipActive = isActive;
    sessionStorage.setItem('membershipActive', isActive ? 'true' : 'false');

    // If membership status changed, clear cart and notify user
    if (wasActive !== isActive) {
      console.log('[MembershipManager] Membership status changed!');
      if (!wasActive && isActive) {
        await this.clearCartWithMessage('Membership added! Please re-add your items to get member pricing.');
      } else if (wasActive && !isActive) {
        console.log('[MembershipManager] Membership removed - clearing cart');
        await this.clearCartWithMessage('Membership removed. Please re-add your items at regular pricing.');
      }
    } else {
      // Status didn't change, but validate cart has correct variants
      console.log('[MembershipManager] Status unchanged - validating cart');
      await this.validateCartNow();
    }

    // Update UI
    this.updateUIBasedOnMembership();
  }

  async validateCartNow() {
    // Check if we should skip validation (just added membership from popup)
    const skipValidationTime = sessionStorage.getItem('skipValidation');
    if (skipValidationTime) {
      const elapsed = Date.now() - parseInt(skipValidationTime);
      if (elapsed < 5000) { // Skip for 5 seconds
        return;
      }
      sessionStorage.removeItem('skipValidation');
    }

    if (this.membershipActive) {
      await this.checkForWrongVariantsWithMembership();
    } else {
      await this.checkForMemberVariantsWithoutMembership();
    }
  }

  async clearCartWithMessage(message) {
    try {
      console.log('[MembershipManager] Clearing cart with message:', message);
      await fetch('/cart/clear.js', { method: 'POST' });
      alert(message);
      // Immediate reload for speed
      window.location.reload(true);
    } catch (error) {
      console.error('[ERROR] Clear cart failed:', error);
      window.location.reload(true);
    }
  }

  async checkForMemberVariantsWithoutMembership() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      for (const item of cart.items) {
        if (item.product_type === 'Membership' || (item.tags && item.tags.includes('site-membership'))) {
          continue;
        }

        const hasMemberOption = item.variant_title &&
                               (item.variant_title.includes('Member') && !item.variant_title.includes('Non-Member'));

        const hasMemberInOptions = item.options_with_values &&
                                  item.options_with_values.some(opt => opt.name === 'Membership' && opt.value === 'Member');

        if (hasMemberOption || hasMemberInOptions) {
          await this.clearCartWithMessage('Member-priced items cannot be purchased without membership. Cart has been cleared. Please add membership first or select regular-priced items.');
          return;
        }
      }
    } catch (error) {
      console.error('[ERROR] Check member variants failed:', error);
    }
  }

  async checkForWrongVariantsWithMembership() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      for (const item of cart.items) {
        if (item.product_type === 'Membership' || (item.tags && item.tags.includes('site-membership'))) {
          continue;
        }

        const hasNonMemberOption = item.variant_title &&
                                   item.variant_title.includes('Non-Member');

        const hasNonMemberInOptions = item.options_with_values &&
                                     item.options_with_values.some(opt => opt.name === 'Membership' && opt.value === 'Non-Member');

        if (hasNonMemberOption || hasNonMemberInOptions) {
          await this.clearCartWithMessage('You have membership! Cart has been cleared. Please re-add your items to get member pricing.');
          return;
        }
      }
    } catch (error) {
      console.error('[ERROR] Check wrong variants failed:', error);
    }
  }

  updateUIBasedOnMembership() {
    const allCards = document.querySelectorAll('[data-membership-card]');

    allCards.forEach(card => {
      if (this.membershipActive) {
        if (card.dataset.membershipCard === 'non-member') {
          // Disable non-member card
          card.classList.add('membership-card--disabled');
          card.classList.remove('membership-card--selected');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.disabled = true;
            radio.checked = false;
          }
        } else if (card.dataset.membershipCard === 'member') {
          // Lock and auto-select member card
          card.classList.add('membership-card--locked', 'membership-card--selected');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.disabled = true;
            radio.checked = true;
          }
          // Update the variant selection to member variant
          const variantId = card.dataset.variantId;
          if (variantId) {
            this.updateSelectedVariant(variantId);
          }
        }
      } else {
        // Reset to normal state
        card.classList.remove('membership-card--disabled', 'membership-card--locked');
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.disabled = false;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.membershipManager = new MembershipManager();
  });
} else {
  window.membershipManager = new MembershipManager();
}
